import React, { useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Card, Navbar, Modal, Alert } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './../AuthContext';
import Footer from './../Footer';
import axios from 'axios';
import { site_url } from "../config.js";
import { parseStoredAccessToken } from '../parseStoredAccessToken.js';
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles.css";

function Index() {
  const { user, fulluser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Bank Account Details
  const [bankAccount, setBankAccount] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [bankName, setBankName] = useState('');
  
  // UPI Details
  const [upiId, setUpiId] = useState('');
  const [preferredMethod, setPreferredMethod] = useState('bank'); // 'bank' or 'upi'

  const getToken = () => parseStoredAccessToken(user);

  // Clean up modal backdrops (must be before any conditional returns)
  useEffect(() => {
    const backdrops = document.querySelectorAll(".modal-backdrop");
    backdrops.forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
  }, [location]);

  // Fetch bank details
  useEffect(() => {
    // Check authentication
    const token = getToken();
    if (!token) {
      navigate('/signin');
      return;
    }
    
    // Fetch bank details
    const fetchBankDetails = async () => {
      setFetching(true);
      try {
        const res = await axios.get(site_url + "/bank-details", {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        });
        if (res.data && res.data.status === 'success' && res.data.data) {
          const data = res.data.data;
          setBankAccount(data.bank_account || '');
          setIfscCode(data.ifsc_code || '');
          setAccountHolderName(data.account_holder_name || '');
          setBankName(data.bank_name || '');
          setUpiId(data.upi_id || '');
          setPreferredMethod(data.preferred_method || 'bank');
        }
      } catch (err) {
        console.error("Bank Details Error:", err.response?.data || err.message);
        // Don't show error if it's just that no details exist yet (404 or empty response)
        if (err.response?.status && err.response.status !== 404) {
          setMessage({ 
            type: 'warning', 
            text: 'Could not load existing details. You can still add new details.' 
          });
        }
      } finally {
        setFetching(false);
      }
    };

    fetchBankDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setLoading(true);
    setMessage({ type: '', text: '' });

    const token = getToken();
    if (!token) {
      setMessage({ type: 'danger', text: 'Please login to continue' });
      setLoading(false);
      navigate('/signin');
      return;
    }

    // Validation
    if (preferredMethod === 'bank') {
      if (!bankAccount || !ifscCode || !accountHolderName) {
        setMessage({ type: 'danger', text: 'Please fill all bank account details' });
        setLoading(false);
        return;
      }
      if (ifscCode.length !== 11) {
        setMessage({ type: 'danger', text: 'IFSC code must be 11 characters' });
        setLoading(false);
        return;
      }
    } else if (preferredMethod === 'upi') {
      if (!upiId) {
        setMessage({ type: 'danger', text: 'Please enter UPI ID' });
        setLoading(false);
        return;
      }
      // Basic UPI validation
      if (!upiId.includes('@')) {
        setMessage({ type: 'danger', text: 'Invalid UPI ID format (e.g., name@paytm)' });
        setLoading(false);
        return;
      }
    }

    try {
      const res = await axios.post(
        site_url + "/save-bank-details",
        {
          bank_account: bankAccount,
          ifsc_code: ifscCode.toUpperCase(),
          account_holder_name: accountHolderName,
          bank_name: bankName,
          upi_id: upiId,
          preferred_method: preferredMethod
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        }
      );

      if (res.data.status === 'success') {
        setMessage({ type: 'success', text: 'Bank/UPI details saved successfully!' });
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        setMessage({ type: 'danger', text: res.data.message || 'Failed to save details' });
      }
    } catch (err) {
      console.error("Save Bank Details Error:", err.response?.data || err.message);
      setMessage({ 
        type: 'danger', 
        text: err.response?.data?.message || 'Something went wrong. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  // If no user, show loading (will redirect)
  const token = getToken();
  if (!token) {
    return (
      <div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: '#666' }}>Redirecting...</p>
        </div>
      </div>
    );
  }

  // Show loading state while fetching
  if (fetching) {
    return (
      <div>
        <Navbar expand="lg" className="navbar px-3">
          <Navbar.Brand className="fw-bold text-white">
            <center>
              <FaArrowLeft 
                onClick={goBack} 
                className="fs-5 text-white notificaton-icon" 
                style={{ position: 'absolute', left: '15px', cursor: 'pointer' }} 
              /> 
              Bank/UPI Details
            </center>
          </Navbar.Brand>
        </Navbar>
        <div id="appCapsule" className='mt-4'>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3" style={{ color: '#666' }}>Loading bank details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar expand="lg" className="navbar px-3">
        <Navbar.Brand className="fw-bold text-white">
          <center>
            <FaArrowLeft 
              onClick={goBack} 
              className="fs-5 text-white notificaton-icon" 
              style={{ position: 'absolute', left: '15px', cursor: 'pointer' }} 
            /> 
            Bank/UPI Details
          </center>
        </Navbar.Brand>
      </Navbar>

      <div id="appCapsule" className='mt-4' style={{ paddingBottom: '120px', minHeight: 'calc(100vh - 56px)' }}>
        {message.text && (
          <div className={`alert alert-${message.type} mx-3 mt-3`} role="alert">
            {message.text}
          </div>
        )}

        <div className="listview-title mt-1">Preferred Payout Method</div>
        <ul className="listview image-listview text inset">
          <li>
            <a href="#" className="item" onClick={(e) => { e.preventDefault(); setPreferredMethod('bank'); }}>
              <div className="in">
                <div>
                  <Form.Check
                    type="radio"
                    label="Bank Account"
                    name="payoutMethod"
                    id="bankMethod"
                    checked={preferredMethod === 'bank'}
                    onChange={() => setPreferredMethod('bank')}
                    style={{ display: 'inline-block', marginRight: '10px' }}
                  />
                </div>
              </div>
            </a>
          </li>
          <li>
            <a href="#" className="item" onClick={(e) => { e.preventDefault(); setPreferredMethod('upi'); }}>
              <div className="in">
                <div>
                  <Form.Check
                    type="radio"
                    label="UPI"
                    name="payoutMethod"
                    id="upiMethod"
                    checked={preferredMethod === 'upi'}
                    onChange={() => setPreferredMethod('upi')}
                    style={{ display: 'inline-block', marginRight: '10px' }}
                  />
                </div>
              </div>
            </a>
          </li>
        </ul>

        {preferredMethod === 'bank' ? (
          <>
            <div className="listview-title mt-1">Bank Account Details</div>
            <ul className="listview image-listview text inset">
              <li>
                <div className="item">
                  <div className="in">
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>Account Holder Name *</div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Account Holder Name"
                      value={accountHolderName}
                      onChange={(e) => setAccountHolderName(e.target.value)}
                      required
                      style={{ border: 'none', padding: '5px 0', background: 'transparent', width: '100%' }}
                    />
                  </div>
                </div>
              </li>
              <li>
                <div className="item">
                  <div className="in">
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>Bank Account Number *</div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Bank Account Number"
                      value={bankAccount}
                      onChange={(e) => setBankAccount(e.target.value.replace(/\D/g, ''))}
                      required
                      minLength={9}
                      maxLength={18}
                      style={{ border: 'none', padding: '5px 0', background: 'transparent', width: '100%' }}
                    />
                    <small className="text-muted" style={{ fontSize: '12px' }}>9-18 digits</small>
                  </div>
                </div>
              </li>
              <li>
                <div className="item">
                  <div className="in">
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>IFSC Code *</div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter IFSC Code (e.g., HDFC0001234)"
                      value={ifscCode}
                      onChange={(e) => setIfscCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      required
                      maxLength={11}
                      style={{ border: 'none', padding: '5px 0', background: 'transparent', width: '100%' }}
                    />
                    <small className="text-muted" style={{ fontSize: '12px' }}>11 characters</small>
                  </div>
                </div>
              </li>
              <li>
                <div className="item">
                  <div className="in">
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>Bank Name</div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Bank Name (Optional)"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      style={{ border: 'none', padding: '5px 0', background: 'transparent', width: '100%' }}
                    />
                  </div>
                </div>
              </li>
            </ul>
          </>
        ) : (
          <>
            <div className="listview-title mt-1">UPI Details</div>
            <ul className="listview image-listview text inset">
              <li>
                <div className="item">
                  <div className="in">
                    <div style={{ marginBottom: '8px', fontWeight: '500' }}>UPI ID *</div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter UPI ID (e.g., name@paytm)"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value.toLowerCase())}
                      required
                      style={{ border: 'none', padding: '5px 0', background: 'transparent', width: '100%' }}
                    />
                    <small className="text-muted" style={{ fontSize: '12px' }}>Format: name@paytm, name@phonepe</small>
                  </div>
                </div>
              </li>
            </ul>
          </>
        )}

        <div className="mx-3 mt-3 mb-3">
          <div className="alert alert-info" role="alert" style={{ fontSize: '13px', padding: '10px' }}>
            <strong>Note:</strong> These details will be used for processing your withdrawal requests. 
            Please ensure all information is correct.
          </div>
        </div>

        <div className="listview-title mt-1">Actions</div>
        <ul className="listview image-listview text inset mb-4">
          <li>
            <a 
              href="#" 
              className="item" 
              onClick={(e) => { 
                e.preventDefault(); 
                handleSubmit(e);
              }}
              style={{ 
                backgroundColor: loading ? '#ccc' : '#009688',
                color: 'white',
                fontWeight: 'bold',
                display: 'block'
              }}
            >
              <div className="in">
                <div style={{ fontWeight: '600', color: 'white', textAlign: 'center', width: '100%' }}>
                  {loading ? 'Saving...' : 'Save Bank/UPI Details'}
                </div>
              </div>
            </a>
          </li>
          <li>
            <a 
              href="#" 
              className="item" 
              onClick={(e) => { e.preventDefault(); goBack(); }}
              style={{ textAlign: 'center' }}
            >
              <div className="in">
                <div>Cancel</div>
              </div>
            </a>
          </li>
        </ul>
      </div>

      <Footer />
    </div>
  );
}

export default Index;

